/**
 * Transaction Search Mixin
 *
 * Provides Alpine.js state and methods for searching transactions within
 * account slideovers. Searches server-side across all transactions (native
 * and legacy) and navigates to matches within the infinite-scroll list.
 *
 * Usage:
 *   import { transactionSearchMixin } from './transaction_search_mixin'
 *   return { ...transactionSearchMixin('landlord'), ...otherState }
 */
const PLURALS = { landlord: 'landlords', tenancy: 'tenancies', contractor: 'contractors', agency: 'agencies', deposit_scheme: 'deposit_schemes' }

export function transactionSearchMixin(accountableType) {
  const pluralType = PLURALS[accountableType] || `${accountableType}s`

  return {
    // Search state
    searchOpen: false,
    searchQuery: '',
    searchMatches: [],
    searchMatchIndex: -1,
    searchLoading: false,
    searchTotalMatches: 0,
    highlightedTxnId: null,
    _searchNavigating: false,

    toggleSearch() {
      this.searchOpen = !this.searchOpen
      if (this.searchOpen) {
        this.$nextTick(() => this.$refs.searchInput?.focus())
      } else {
        this.clearSearch()
      }
    },

    clearSearch() {
      this.searchQuery = ''
      this.searchMatches = []
      this.searchMatchIndex = -1
      this.searchLoading = false
      this.searchTotalMatches = 0
      this.highlightedTxnId = null
      this._searchNavigating = false
    },

    async performSearch() {
      const query = this.searchQuery.trim()
      if (!query) {
        this.searchMatches = []
        this.searchMatchIndex = -1
        this.highlightedTxnId = null
        return
      }

      this.searchLoading = true

      try {
        const id = this._searchAccountableId()
        const params = new URLSearchParams({ query })
        const response = await fetch(
          `/accounting/accounts/${pluralType}/${id}/search.json?${params}`,
          {
            headers: {
              'Accept': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
          }
        )

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.searchMatches = data.matches || []
        this.searchTotalMatches = data.total_matches || 0

        if (this.searchMatches.length > 0) {
          this.searchMatchIndex = 0
          this.navigateToMatch(0)
        } else {
          this.searchMatchIndex = -1
          this.highlightedTxnId = null
        }
      } catch (error) {
        console.error('Transaction search failed:', error)
        this.searchMatches = []
        this.searchMatchIndex = -1
      } finally {
        this.searchLoading = false
      }
    },

    nextMatch() {
      if (this.searchMatches.length === 0) return
      const next = (this.searchMatchIndex + 1) % this.searchMatches.length
      this.navigateToMatch(next)
    },

    prevMatch() {
      if (this.searchMatches.length === 0) return
      const prev = (this.searchMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length
      this.navigateToMatch(prev)
    },

    async navigateToMatch(index) {
      if (index < 0 || index >= this.searchMatches.length) return

      this.searchMatchIndex = index
      const match = this.searchMatches[index]
      this.highlightedTxnId = match.id

      // Check if the transaction is already loaded in the DOM
      const existing = this._findTxnElement(match.id)
      if (existing) {
        this._scrollToElement(existing)
        return
      }

      // Transaction not loaded yet — load pages until it appears
      this._searchNavigating = true
      const maxAttempts = 20 // Safety limit
      let attempts = 0

      while (this.hasMore && attempts < maxAttempts) {
        attempts++
        this.page++
        await this._fetchDataAsync()

        await this.$nextTick()
        const el = this._findTxnElement(match.id)
        if (el) {
          this._scrollToElement(el)
          this._searchNavigating = false
          return
        }
      }

      this._searchNavigating = false
    },

    // Scroll to a specific transaction by ID, loading pages if needed.
    // Used by the AI assistant when transaction references are clicked.
    _scrollToTxnActive: false,
    async scrollToTransaction(txnId) {
      if (this._scrollToTxnActive) return
      this._scrollToTxnActive = true

      this.highlightedTxnId = txnId
      window.dispatchEvent(new CustomEvent('scroll-to-transaction-started', { detail: { id: txnId } }))

      const delay = (ms) => new Promise(r => setTimeout(r, ms))

      try {
        // Check if already in DOM
        let el = this._findTxnElement(txnId)
        if (el) { this._scrollToElement(el); return }

        // Load pages, checking DOM after each
        while (this.hasMore) {
          this.page++
          await this._fetchDataAsync()
          await delay(100)

          el = this._findTxnElement(txnId)
          if (el) { this._scrollToElement(el); return }
        }

        // All pages loaded — keep checking DOM until Alpine finishes rendering
        for (let i = 0; i < 50; i++) {
          await delay(100)
          el = this._findTxnElement(txnId)
          if (el) { this._scrollToElement(el); return }
        }
      } finally {
        this._scrollToTxnActive = false
        window.dispatchEvent(new CustomEvent('scroll-to-transaction-done', { detail: { id: txnId } }))
      }
    },

    _findTxnElement(txnId) {
      const container = this.$refs.scrollContainer || this.$el
      return container.querySelector(`[data-txn-id="${txnId}"]`)
    },

    _scrollToElement(el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    // Promisified version of fetchData for sequential loading
    _fetchDataAsync() {
      return new Promise((resolve) => {
        this.loadingMore = true

        const id = this._searchAccountableId()
        const params = new URLSearchParams({ page: this.page })

        fetch(`/accounting/accounts/${pluralType}/${id}.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })
          .then(response => response.json())
          .then(data => {
            const newLines = data.transactions?.lines || []
            this.transactions = [...this.transactions, ...newLines]
            this.hasMore = data.transactions?.pagination?.has_more || false
            this.loadingMore = false
            resolve()
          })
          .catch(() => {
            this.loadingMore = false
            resolve()
          })
      })
    },

    _searchAccountableId() {
      if (accountableType === 'landlord') return this.landlordId
      if (accountableType === 'tenancy') return this.tenancyId
      if (accountableType === 'contractor') return this.contractorId
      if (accountableType === 'agency') return this.agencyId
      if (accountableType === 'deposit_scheme') return this.schemeId
      return null
    }
  }
}
